import React, { useState, useRef } from 'react';
import {
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
    MDBModalFooter,
    MDBBtn,
    MDBIcon,
    MDBProgress,
    MDBAlert
} from 'mdb-react-ui-kit';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { importWhatsAppTemplates } from '../../services/apiServices';

const ImportTemplateModal = ({ show, onClose, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: instructions, 2: upload, 3: results
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [importResults, setImportResults] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const downloadSampleTemplate = async () => {
        try {
            // Create a new workbook
            const workbook = new ExcelJS.Workbook();

            // Add metadata
            workbook.creator = 'EasyMembr';
            workbook.lastModifiedBy = 'EasyMembr';
            workbook.created = new Date();
            workbook.modified = new Date();

            // Create Template Data worksheet
            const templateSheet = workbook.addWorksheet('Template Data');

            // Define columns
            templateSheet.columns = [
                { header: 'Template Name', key: 'templateName', width: 25 },
                { header: 'Category', key: 'category', width: 15 },
                { header: 'Language', key: 'language', width: 15 },
                { header: 'Header Format', key: 'headerFormat', width: 15 },
                { header: 'Header Text', key: 'headerText', width: 30 },
                { header: 'Header Examples', key: 'headerExamples', width: 30 },
                { header: 'Body Text', key: 'bodyText', width: 40 },
                { header: 'Body Examples', key: 'bodyExamples', width: 30 },
                { header: 'Footer Text', key: 'footerText', width: 25 },
                { header: 'Button 1 Type', key: 'button1Type', width: 15 },
                { header: 'Button 1 Text', key: 'button1Text', width: 20 },
                { header: 'Button 1 URL/Phone', key: 'button1Value', width: 25 },
                { header: 'Button 2 Type', key: 'button2Type', width: 15 },
                { header: 'Button 2 Text', key: 'button2Text', width: 20 },
                { header: 'Button 2 URL/Phone', key: 'button2Value', width: 25 },
                { header: 'Button 3 Type', key: 'button3Type', width: 15 },
                { header: 'Button 3 Text', key: 'button3Text', width: 20 },
                { header: 'Button 3 URL/Phone', key: 'button3Value', width: 25 }
            ];

            // Style the header row
            templateSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            templateSheet.getRow(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF0078D7' }
            };
            templateSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

            // Add sample data
            const sampleData = [
                {
                    templateName: 'welcome_message',
                    category: 'UTILITY',
                    language: 'en',
                    headerFormat: 'TEXT',
                    headerText: 'Welcome {{1}}!',
                    headerExamples: 'John Doe',
                    bodyText: 'Thank you for joining {{1}}. Your membership ID is {{2}}.',
                    bodyExamples: 'Our Gym, MEM123',
                    footerText: 'Reply STOP to opt out',
                    button1Type: 'QUICK_REPLY',
                    button1Text: 'Learn More',
                    button1Value: '',
                    button2Type: 'URL',
                    button2Text: 'Visit Website',
                    button2Value: 'https://example.com',
                    button3Type: 'PHONE_NUMBER',
                    button3Text: 'Call Us',
                    button3Value: '+1234567890'
                },
                {
                    templateName: 'payment_confirmation',
                    category: 'UTILITY',
                    language: 'en',
                    headerFormat: 'NONE',
                    headerText: '',
                    headerExamples: '',
                    bodyText: 'Your payment of {{1}} has been received. Transaction ID: {{2}}',
                    bodyExamples: '$50, TXN123456',
                    footerText: 'Thank you for your payment',
                    button1Type: 'URL',
                    button1Text: 'View Receipt',
                    button1Value: 'https://example.com/receipt/{{1}}',
                    button2Type: '',
                    button2Text: '',
                    button2Value: '',
                    button3Type: '',
                    button3Text: '',
                    button3Value: ''
                }
            ];

            // Add data rows with alternating colors
            sampleData.forEach((data, index) => {
                const row = templateSheet.addRow(data);

                // Alternate row colors
                if (index % 2 === 0) {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF5F5F5' }
                        };
                    });
                }
            });

            // Add data validation for dropdowns
            templateSheet.dataValidations.add('B2:B1000', {
                type: 'list',
                allowBlank: false,
                formulae: ['"UTILITY,MARKETING,AUTHENTICATION"']
            });

            templateSheet.dataValidations.add('C2:C1000', {
                type: 'list',
                allowBlank: false,
                formulae: ['"en,en_US,en_GB,es,fr,de,hi"']
            });

            templateSheet.dataValidations.add('D2:D1000', {
                type: 'list',
                allowBlank: true,
                formulae: ['"NONE,TEXT"']
            });

            // Create Instructions worksheet
            const instructionsSheet = workbook.addWorksheet('Instructions', { properties: { tabColor: { argb: 'FFE6E6E6' } } });

            instructionsSheet.columns = [
                { header: 'Field', key: 'field', width: 25 },
                { header: 'Description', key: 'description', width: 50 },
                { header: 'Required', key: 'required', width: 15 },
                { header: 'Example', key: 'example', width: 30 }
            ];

            // Style instructions header
            instructionsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            instructionsSheet.getRow(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF4CAF50' }
            };

            const instructions = [
                { field: 'Template Name', description: 'Unique name for the template (max 512 chars)', required: 'Yes', example: 'welcome_message' },
                { field: 'Category', description: 'UTILITY, MARKETING, or AUTHENTICATION', required: 'Yes', example: 'UTILITY' },
                { field: 'Language', description: 'en, en_US, en_GB, es, fr, de, hi', required: 'Yes', example: 'en' },
                { field: 'Header Format', description: 'NONE or TEXT', required: 'If header used', example: 'TEXT' },
                { field: 'Header Text', description: 'Header content with variables like {{1}}', required: 'If format is TEXT', example: 'Welcome {{1}}!' },
                { field: 'Header Examples', description: 'Comma-separated examples for header variables', required: 'If header has variables', example: 'John Doe' },
                { field: 'Body Text', description: 'Main message content with variables like {{1}}', required: 'Yes', example: 'Thank you for joining {{1}}.' },
                { field: 'Body Examples', description: 'Comma-separated examples for body variables', required: 'If body has variables', example: 'Our Gym' },
                { field: 'Footer Text', description: 'Footer content (max 60 chars)', required: 'No', example: 'Reply STOP to opt out' },
                { field: 'Button 1-3 Type', description: 'QUICK_REPLY, URL, or PHONE_NUMBER', required: 'If button used', example: 'QUICK_REPLY' },
                { field: 'Button 1-3 Text', description: 'Button text (max 25 chars)', required: 'If button used', example: 'Learn More' },
                { field: 'Button 1-3 URL/Phone', description: 'URL or phone number for button', required: 'If button type requires it', example: 'https://example.com' }
            ];

            instructions.forEach((instruction, index) => {
                const row = instructionsSheet.addRow(instruction);
                if (index % 2 === 0) {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF9F9F9' }
                        };
                    });
                }
            });

            // Add notes section
            instructionsSheet.addRow([]);
            const notesRow = instructionsSheet.addRow({ field: 'NOTES:', description: 'Important guidelines for template creation' });
            notesRow.font = { bold: true, color: { argb: 'FFFF0000' } };

            const notes = [
                '• Template names must be unique and use only letters, numbers, and underscores',
                '• IMPORTANT: Use ONLY numbered variables like {{1}}, {{2}}, {{3}} - NOT named variables like {{name}}',
                '• Variables must be sequential starting from {{1}}',
                '• Provide examples for each variable in the same order (comma-separated)',
                '• Maximum 3 buttons per template',
                '• URL buttons must include full URL with https://',
                '• Phone numbers must be in international format (+1234567890)',
                '• Quick reply buttons: 1-3 characters recommended',
                '• Leave unused buttons empty',
                '• Do not leave empty rows in the template - they will cause validation errors'
            ];

            notes.forEach(note => {
                instructionsSheet.addRow({ field: '', description: note });
            });

            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'whatsapp_template_sample.xlsx');

        } catch (error) {
            console.error('Error creating sample file:', error);
            setError('Failed to generate sample file. Please try again.');
        }
    };

    const validateTemplateData = (data) => {
        console.log('Validating data:', data);
        const errors = [];
        const validCategories = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
        const validLanguages = ['en', 'en_US', 'en_GB', 'es', 'fr', 'de', 'hi'];

        data.forEach((row, index) => {
            const rowNum = index + 2; // +2 because Excel rows start at 1 and header is row 1

            // Helper function to check if a field has real content
            const hasRealContent = (value) => {
                if (!value) return false;
                const strValue = value.toString().trim();
                return strValue && strValue !== '' && strValue !== ' ';
            };

            // Get trimmed values
            const templateName = row['Template Name']?.toString().trim() || '';
            const category = row['Category']?.toString().trim() || '';
            const language = row['Language']?.toString().trim() || '';
            const bodyText = row['Body Text']?.toString().trim() || '';

            // Skip if this is clearly an empty row (no real content in required fields)
            if (!templateName && !category && !language && !bodyText) {
                console.log(`Row ${rowNum} appears to be empty, skipping validation`);
                return;
            }

            // Check if this is a row with just spaces
            if (bodyText === ' ' && !templateName && !category && !language) {
                console.log(`Row ${rowNum} has only spaces, skipping validation`);
                return;
            }

            // Required fields validation
            if (!templateName) {
                errors.push(`Row ${rowNum}: Template Name is required`);
            } else if (!/^[a-zA-Z0-9_]+$/.test(templateName)) {
                errors.push(`Row ${rowNum}: Template Name can only contain letters, numbers, and underscores`);
            }

            if (!category || !validCategories.includes(category)) {
                errors.push(`Row ${rowNum}: Category must be one of: ${validCategories.join(', ')}`);
            }

            if (!language || !validLanguages.includes(language)) {
                errors.push(`Row ${rowNum}: Language must be one of: ${validLanguages.join(', ')}`);
            }

            if (!bodyText || bodyText === ' ') {
                errors.push(`Row ${rowNum}: Body Text is required`);
            }

            // Check for named variables (which Meta doesn't accept)
            const namedVarRegex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
            if (namedVarRegex.test(bodyText)) {
                errors.push(`Row ${rowNum}: Body Text contains named variables like {{variable}}. Meta requires numbered variables like {{1}}, {{2}}, etc.`);
            }

            // Check variable format and sequential numbering
            const varRegex = /\{\{(\d+)\}\}/g;
            const matches = [...bodyText.matchAll(varRegex)];
            if (matches.length > 0) {
                // Check if variables are sequential starting from 1
                const varNumbers = matches.map(m => parseInt(m[1])).sort((a, b) => a - b);
                for (let i = 0; i < varNumbers.length; i++) {
                    if (varNumbers[i] !== i + 1) {
                        errors.push(`Row ${rowNum}: Body variables must be sequential starting from {{1}}. Found {{${varNumbers[i]}}} but expected {{${i + 1}}}`);
                        break;
                    }
                }
            }

            // Check examples match variable count
            if (bodyText && bodyText !== ' ') {
                const varCount = matches.length;
                if (varCount > 0) {
                    const examples = row['Body Examples']?.toString().trim() || '';
                    if (!examples) {
                        errors.push(`Row ${rowNum}: Body Examples are required when using variables`);
                    } else {
                        const exampleCount = examples.split(',').filter(e => e.trim()).length;
                        if (exampleCount !== varCount) {
                            errors.push(`Row ${rowNum}: Body has ${varCount} variable(s) but ${exampleCount} example(s) provided`);
                        }
                    }
                }
            }

            // Only continue validation if we have the basic required fields
            if (templateName && category && validCategories.includes(category) && 
                language && validLanguages.includes(language) && bodyText && bodyText !== ' ') {
                
                // Header validation
                const headerFormat = row['Header Format']?.toString().trim() || 'NONE';
                if (headerFormat && !['NONE', 'TEXT'].includes(headerFormat)) {
                    errors.push(`Row ${rowNum}: Header Format must be NONE or TEXT`);
                }

                if (headerFormat === 'TEXT' && !hasRealContent(row['Header Text'])) {
                    errors.push(`Row ${rowNum}: Header Text is required when Header Format is TEXT`);
                }

                // Check header for named variables
                const headerText = row['Header Text']?.toString() || '';
                if (headerText && namedVarRegex.test(headerText)) {
                    errors.push(`Row ${rowNum}: Header Text contains named variables. Use numbered variables like {{1}} instead.`);
                }

                // Button validation
                for (let i = 1; i <= 3; i++) {
                    const btnType = row[`Button ${i} Type`]?.toString().trim();
                    const btnText = row[`Button ${i} Text`]?.toString().trim();
                    const btnValue = row[`Button ${i} URL/Phone`]?.toString().trim();

                    if (btnType && !['QUICK_REPLY', 'URL', 'PHONE_NUMBER', ''].includes(btnType)) {
                        errors.push(`Row ${rowNum}: Button ${i} Type must be QUICK_REPLY, URL, or PHONE_NUMBER`);
                    }
                    if (btnType && !btnText) {
                        errors.push(`Row ${rowNum}: Button ${i} Text is required when button type is provided`);
                    }
                    if (btnType === 'URL' && !btnValue) {
                        errors.push(`Row ${rowNum}: Button ${i} URL is required for URL button type`);
                    }
                    if (btnType === 'URL' && btnValue && !btnValue.startsWith('http')) {
                        errors.push(`Row ${rowNum}: Button ${i} URL must start with http:// or https://`);
                    }
                    if (btnType === 'PHONE_NUMBER' && !btnValue) {
                        errors.push(`Row ${rowNum}: Button ${i} Phone is required for PHONE_NUMBER button type`);
                    }
                    if (btnType === 'PHONE_NUMBER' && btnValue && !btnValue.startsWith('+')) {
                        errors.push(`Row ${rowNum}: Button ${i} Phone must be in international format (+1234567890)`);
                    }
                    if (btnText && btnText.length > 25) {
                        errors.push(`Row ${rowNum}: Button ${i} Text must not exceed 25 characters`);
                    }
                }

                // Character limits
                if (row['Header Text']?.toString().trim() && row['Header Text'].length > 60) {
                    errors.push(`Row ${rowNum}: Header Text must not exceed 60 characters`);
                }
                if (bodyText.length > 1024) {
                    errors.push(`Row ${rowNum}: Body Text must not exceed 1024 characters (current: ${bodyText.length})`);
                }
                if (row['Footer Text']?.toString().trim() && row['Footer Text'].length > 60) {
                    errors.push(`Row ${rowNum}: Footer Text must not exceed 60 characters`);
                }
            }
        });

        console.log('Validation errors:', errors);
        return errors;
    };

    const parseExcelFile = async (file) => {
        console.log('Starting to parse file:', file.name);
        
        try {
            const workbook = new ExcelJS.Workbook();
            const buffer = await file.arrayBuffer();
            console.log('File buffer size:', buffer.byteLength);
            
            await workbook.xlsx.load(buffer);
            console.log('Workbook loaded successfully');

            // Get first worksheet
            const worksheet = workbook.getWorksheet(1);
            if (!worksheet) {
                throw new Error('No worksheet found in the Excel file');
            }
            console.log('Worksheet found:', worksheet.name);

            // Get headers from first row
            const headers = [];
            const headerRow = worksheet.getRow(1);
            headerRow.eachCell((cell) => {
                headers.push(cell.value?.toString() || '');
            });
            console.log('Headers found:', headers);

            // Parse data rows
            const data = [];
            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return; // Skip header row

                const rowData = {};
                let hasRequiredFields = false;
                let hasAnyData = false;
                
                row.eachCell((cell, colNumber) => {
                    const header = headers[colNumber - 1];
                    if (header) {
                        const value = cell.value?.toString() || '';
                        const trimmedValue = value.trim();
                        
                        // Store the value
                        rowData[header] = value;
                        
                        // Check if this is a required field with actual content
                        if (header === 'Template Name' || header === 'Category' || 
                            header === 'Language' || header === 'Body Text') {
                            if (trimmedValue && trimmedValue.length > 0 && trimmedValue !== ' ') {
                                hasRequiredFields = true;
                            }
                        }
                        
                        // Check if any field has actual content (not just spaces)
                        if (trimmedValue && trimmedValue.length > 0 && trimmedValue !== ' ') {
                            hasAnyData = true;
                        }
                    }
                });

                // Only add rows that have required fields with actual content
                if (hasRequiredFields) {
                    data.push(rowData);
                    console.log(`Row ${rowNumber} has valid data:`, {
                        name: rowData['Template Name'],
                        category: rowData['Category'],
                        language: rowData['Language'],
                        bodyPreview: rowData['Body Text']?.substring(0, 30)
                    });
                } else if (hasAnyData) {
                    // Row has some data but missing required fields
                    console.log(`Row ${rowNumber} has incomplete data (missing required fields), skipping`);
                } else {
                    console.log(`Row ${rowNumber} is completely empty, skipping`);
                }
            });

            console.log(`Total valid rows parsed: ${data.length}`);
            return data;
        } catch (error) {
            console.error('Error parsing Excel file:', error);
            throw new Error('Failed to parse Excel file: ' + error.message);
        }
    };

    const transformImportData = (rawData) => {
        console.log('Transforming data:', rawData);
        
        return rawData.map((row, index) => {
            console.log(`Transforming row ${index + 1}:`, row);
            
            // Get the body text and fix variable formatting
            let bodyText = row['Body Text']?.trim() || '';
            
            // Fix named variables to use numbered format {{1}}, {{2}}, etc.
            // This regex finds any {{something}} where 'something' is not a number
            const namedVarRegex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
            let match;
            const namedVariables = [];
            
            // First, collect all named variables
            while ((match = namedVarRegex.exec(bodyText)) !== null) {
                namedVariables.push(match[1]);
            }
            
            // Replace named variables with numbered ones
            if (namedVariables.length > 0) {
                console.log(`Found named variables in template ${index + 1}:`, namedVariables);
                namedVariables.forEach((varName, idx) => {
                    const varNumber = idx + 1;
                    bodyText = bodyText.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), `{{${varNumber}}}`);
                });
                console.log(`Fixed body text:`, bodyText);
            }
            
            // Check if variables are sequential starting from 1
            const varRegex = /\{\{(\d+)\}\}/g;
            const matches = [...bodyText.matchAll(varRegex)];
            const varNumbers = matches.map(m => parseInt(m[1])).sort((a, b) => a - b);
            
            // Renumber variables if they're not sequential
            if (varNumbers.length > 0) {
                let needsRenumbering = false;
                for (let i = 0; i < varNumbers.length; i++) {
                    if (varNumbers[i] !== i + 1) {
                        needsRenumbering = true;
                        break;
                    }
                }
                
                if (needsRenumbering) {
                    console.log(`Variables not sequential in template ${index + 1}, renumbering...`);
                    // Create a map of old to new numbers
                    const varMap = {};
                    varNumbers.forEach((oldNum, i) => {
                        varMap[oldNum] = i + 1;
                    });
                    
                    // Replace each variable with its new number
                    Object.keys(varMap).forEach(oldNum => {
                        const newNum = varMap[oldNum];
                        if (oldNum != newNum) {
                            const oldPattern = new RegExp(`\\{\\{${oldNum}\\}\\}`, 'g');
                            bodyText = bodyText.replace(oldPattern, `{{${newNum}}}`);
                        }
                    });
                    console.log(`Renumbered body text:`, bodyText);
                }
            }

            // Fix body examples to match the new variable count
            let bodyExamples = [];
            if (row['Body Examples']) {
                bodyExamples = row['Body Examples'].split(',').map(s => s.trim());
            }
            
            // Ensure examples match variable count
            const finalVarCount = (bodyText.match(/\{\{(\d+)\}\}/g) || []).length;
            if (finalVarCount > 0) {
                while (bodyExamples.length < finalVarCount) {
                    bodyExamples.push(`Example ${bodyExamples.length + 1}`);
                }
                bodyExamples = bodyExamples.slice(0, finalVarCount);
            }

            // Fix header text if present
            let headerText = row['Header Text']?.trim() || '';
            if (headerText) {
                // Fix named variables in header
                const headerNamedVarRegex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
                const headerMatches = [...headerText.matchAll(headerNamedVarRegex)];
                if (headerMatches.length > 0) {
                    console.log(`Found named variables in header:`, headerMatches);
                    // Since header variables are separate from body, we can renumber them starting from 1
                    headerMatches.forEach((match, idx) => {
                        const varName = match[1];
                        const varNumber = idx + 1;
                        headerText = headerText.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), `{{${varNumber}}}`);
                    });
                }
            }

            const template = {
                name: row['Template Name']?.trim(),
                category: row['Category']?.trim(),
                language: row['Language']?.trim(),
                components: {
                    header: {
                        format: row['Header Format']?.trim() || 'NONE',
                        text: headerText,
                        examples: row['Header Examples'] ? row['Header Examples'].split(',').map(s => s.trim()) : []
                    },
                    body: {
                        text: bodyText,
                        examples: bodyExamples
                    },
                    footer: {
                        text: row['Footer Text']?.trim() || ''
                    },
                    buttons: []
                }
            };

            // Add buttons
            for (let i = 1; i <= 3; i++) {
                const btnType = row[`Button ${i} Type`]?.trim();
                const btnText = row[`Button ${i} Text`]?.trim();
                const btnValue = row[`Button ${i} URL/Phone`]?.trim();

                if (btnType && btnText) {
                    const button = {
                        type: btnType,
                        text: btnText
                    };

                    if (btnType === 'URL') {
                        // Fix URL if it contains variables
                        let url = btnValue || 'https://example.com';
                        if (url.includes('{{')) {
                            // Renumber URL variables to match body variables
                            const urlVarRegex = /\{\{(\d+)\}\}/g;
                            const urlMatches = [...url.matchAll(urlVarRegex)];
                            if (urlMatches.length > 0) {
                                // Keep URL variables as they are - they should match body variables
                                console.log(`URL contains variables:`, urlMatches);
                            }
                        }
                        button.url = url;
                    } else if (btnType === 'PHONE_NUMBER') {
                        button.phoneNumber = btnValue || '+1234567890';
                    }

                    template.components.buttons.push(button);
                }
            }

            console.log(`Transformed template ${index + 1}:`, template);
            return template;
        });
    };

    const handleFileUpload = async (e) => {
        console.log('File upload triggered', e.target.files);
        
        const selectedFile = e.target.files[0];
        if (!selectedFile) {
            console.log('No file selected');
            return;
        }
        
        console.log('Selected file details:', {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
            lastModified: selectedFile.lastModified
        });

        // Validate file size (max 10MB)
        if (selectedFile.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }

        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
        ];
        
        // Also accept files with .xlsx extension even if type is not recognized
        const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
        const isValidExtension = ['xlsx', 'xls', 'csv'].includes(fileExtension);
        
        if (!validTypes.includes(selectedFile.type) && !isValidExtension) {
            console.log('Invalid file type:', selectedFile.type, 'Extension:', fileExtension);
            setError('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
            return;
        }

        setFile(selectedFile);
        setError('');

        try {
            setLoading(true);
            setUploadProgress(30);
            console.log('Starting file parsing...');

            // Parse Excel file
            const rawData = await parseExcelFile(selectedFile);
            console.log('Raw parsed data:', rawData);
            
            setUploadProgress(60);

            if (rawData.length === 0) {
                console.log('No valid data found in file');
                setError('The file contains no valid template data. Please check that you have filled in the required fields correctly.');
                setLoading(false);
                setUploadProgress(0);
                return;
            }

            if (rawData.length > 50) {
                setError('Maximum 50 templates can be imported at once');
                setLoading(false);
                setUploadProgress(0);
                return;
            }

            // Validate data
            console.log('Validating data...');
            const validationErrors = validateTemplateData(rawData);
            
            if (validationErrors.length > 0) {
                console.log('Validation errors found:', validationErrors);
                setError('Validation errors:\n' + validationErrors.join('\n'));
                setLoading(false);
                setUploadProgress(0);
                return;
            }

            setUploadProgress(80);
            console.log('Validation passed, transforming data...');

            // Transform data for API
            const templates = transformImportData(rawData);
            console.log('Transformed templates:', templates);
            
            setUploadProgress(100);

            // Store in state for later submission
            const results = {
                templates,
                totalCount: templates.length,
                validationErrors: []
            };
            
            console.log('Setting import results:', results);
            setImportResults(results);
            
            // Move to step 3
            setStep(3);

        } catch (error) {
            console.error('Error in file upload process:', error);
            setError(error.message || 'Failed to parse Excel file. Please check the format.');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (!importResults?.templates.length) {
            console.log('No templates to import');
            return;
        }
        
        console.log('Importing templates:', importResults);

        setLoading(true);
        setError('');

        try {
            const loggedUserInfo = JSON.parse(localStorage.getItem("loggedUserInfo"));
            const gymid = loggedUserInfo?.gymid || "";
            const createdBy = loggedUserInfo?._id || null;

            console.log('Sending import request with:', {
                gymid,
                createdBy,
                templateCount: importResults.templates.length
            });

            // Submit all templates
            const response = await importWhatsAppTemplates({
                gymid,
                createdBy,
                templates: importResults.templates
            });

            console.log('Import response:', response);

            if (response.success) {
                onSuccess();
            } else {
                setError(response.message || 'Import failed');
            }
        } catch (error) {
            console.error('Import error:', error);
            setError(error.message || 'Failed to import templates');
        } finally {
            setLoading(false);
        }
    };

    const resetModal = () => {
        setStep(1);
        setFile(null);
        setError('');
        setImportResults(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    return (
        <MDBModal tabIndex="-1" open={show} onClose={handleClose}>
            <MDBModalDialog size="lg">
                <MDBModalContent>
                    <MDBModalHeader>
                        <MDBModalTitle>
                            <MDBIcon icon="file-import" className="me-2" />
                            Import WhatsApp Templates
                        </MDBModalTitle>
                        <MDBBtn className="btn-close" color="none" onClick={handleClose} />
                    </MDBModalHeader>

                    <MDBModalBody>
                        {error && (
                            <MDBAlert color="danger" className="mb-3" dismissible onClose={() => setError('')}>
                                <MDBIcon icon="exclamation-triangle" className="me-2" />
                                <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
                            </MDBAlert>
                        )}

                        {step === 1 && (
                            <div className="text-center py-4">
                                <MDBIcon icon="file-excel" size="3x" className="text-success mb-3" />
                                <h5>Import Templates from Excel</h5>
                                <p className="text-muted mb-4">
                                    Download the sample template, fill in your template data, and upload the file.
                                    You can import up to 50 templates at once.
                                </p>

                                <div className="d-flex justify-content-center gap-3">
                                    <MDBBtn color="primary" onClick={downloadSampleTemplate}>
                                        <MDBIcon icon="download" className="me-2" />
                                        Download Sample
                                    </MDBBtn>
                                    <MDBBtn color="success" onClick={() => setStep(2)}>
                                        <MDBIcon icon="arrow-right" className="me-2" />
                                        Continue to Upload
                                    </MDBBtn>
                                </div>

                                <div className="mt-4 text-start">
                                    <h6 className="text-danger">⚠️ Important Instructions:</h6>
                                    <ul className="text-muted">
                                        <li className="text-danger">
                                            <strong>USE ONLY NUMBERED VARIABLES:</strong> Use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, etc. 
                                            NOT <code>{'{{name}}'}</code> or <code>{'{{membername}}'}</code>
                                        </li>
                                        <li>Template names must be unique and use only letters, numbers, and underscores</li>
                                        <li>Variables must be sequential starting from <code>{'{{1}}'}</code></li>
                                        <li>Provide examples for each variable (comma-separated) in the same order</li>
                                        <li>Maximum 3 buttons per template</li>
                                        <li>URL buttons must include full URL (https://...)</li>
                                        <li>Phone numbers must be in international format (+1234567890)</li>
                                        <li>Quick reply buttons: 1-3 characters recommended</li>
                                        <li>Leave unused buttons empty</li>
                                        <li className="text-danger">
                                            <strong>Do not leave empty rows in your Excel file.</strong> 
                                            Delete any blank rows before uploading.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="text-center py-4">
                                <div
                                    className="upload-area p-5 border rounded-3 bg-light"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const droppedFile = e.dataTransfer.files[0];
                                        if (droppedFile) {
                                            const event = { target: { files: [droppedFile] } };
                                            handleFileUpload(event);
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".xlsx,.xls,.csv"
                                        onChange={handleFileUpload}
                                        className="d-none"
                                        id="excel-upload"
                                    />
                                    <label htmlFor="excel-upload" className="cursor-pointer" style={{ cursor: 'pointer' }}>
                                        <MDBIcon icon="cloud-upload-alt" size="3x" className="text-primary mb-3" />
                                        <h5>Click to upload or drag and drop</h5>
                                        <p className="text-muted">Excel files only (.xlsx, .xls, .csv) - max 10MB</p>
                                    </label>
                                </div>

                                {loading && (
                                    <div className="mt-4">
                                        <MDBProgress value={uploadProgress} className="mb-2" />
                                        <p className="text-muted">Processing file...</p>
                                    </div>
                                )}

                                <div className="mt-4">
                                    <MDBBtn color="link" onClick={() => setStep(1)}>
                                        <MDBIcon icon="arrow-left" className="me-2" />
                                        Back to Instructions
                                    </MDBBtn>
                                </div>
                            </div>
                        )}

                        {step === 3 && importResults && (
                            <div className="py-3">
                                <h5 className="mb-3">Import Summary</h5>

                                <div className="alert alert-info">
                                    <MDBIcon icon="info-circle" className="me-2" />
                                    Found <strong>{importResults.totalCount}</strong> template(s) ready to import
                                </div>

                                <div className="import-preview mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Template Name</th>
                                                <th>Category</th>
                                                <th>Language</th>
                                                <th>Body Preview</th>
                                                <th>Variables</th>
                                                <th>Buttons</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importResults.templates.map((template, index) => {
                                                const varCount = (template.components.body.text.match(/\{\{(\d+)\}\}/g) || []).length;
                                                return (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <strong>{template.name}</strong>
                                                        </td>
                                                        <td>
                                                            <span className={`badge bg-${template.category === 'UTILITY' ? 'info' : template.category === 'MARKETING' ? 'success' : 'warning'}`}>
                                                                {template.category}
                                                            </span>
                                                        </td>
                                                        <td>{template.language}</td>
                                                        <td>
                                                            <small className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={template.components.body.text}>
                                                                {template.components.body.text.substring(0, 50)}
                                                                {template.components.body.text.length > 50 && '...'}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {varCount > 0 ? (
                                                                <span className="badge bg-primary">
                                                                    {varCount} variable(s)
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">No variables</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {template.components.buttons.length > 0 ? (
                                                                <span className="badge bg-success">
                                                                    {template.components.buttons.length} button(s)
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">None</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="alert alert-warning small">
                                    <MDBIcon icon="info-circle" className="me-2" />
                                    <strong>Note:</strong> Any named variables (like {'{{name}}'}) have been automatically converted to numbered variables ({'{{1}}'}, {'{{2}}'}, etc.) for Meta compatibility.
                                </div>
                            </div>
                        )}
                    </MDBModalBody>

                    <MDBModalFooter>
                        {step === 3 ? (
                            <>
                                <MDBBtn color="secondary" onClick={resetModal}>
                                    <MDBIcon icon="redo" className="me-2" />
                                    Upload Another File
                                </MDBBtn>
                                <MDBBtn
                                    color="success"
                                    onClick={handleImport}
                                    disabled={loading || !importResults?.templates.length}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <MDBIcon icon="check" className="me-2" />
                                            Import {importResults?.totalCount} Template(s)
                                        </>
                                    )}
                                </MDBBtn>
                            </>
                        ) : (
                            <MDBBtn color="secondary" onClick={handleClose}>
                                Cancel
                            </MDBBtn>
                        )}
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
};

export default ImportTemplateModal;